import React, { useCallback, useState } from 'react';
import { Box, Button, ButtonGroup } from '@mui/material';
import ParentForm from '../components/forms/ParentForm';
import ChildrenForm from '../components/forms/ChildrenForm';
import { useUser } from '../contexts/UserContext';
import CustomSnackbar from '../components/elements/CustomSnackbar';
import { GoogleReCaptcha, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';

interface User {
  uid: string;
  fname: string;
  lname: string;
  email: string;
  phone: string;
  payerId: number;
  children: Child[];
}

interface Child {
  id: number;
  fname: string;
  lname: string;
  dob: string;
  gender: string;
  isNew: boolean;
}

const SignInPage: React.FC = () => {
  const { user, saveUser } = useUser();
  const [childrenData, setChildrenData] = useState(user?.children || []);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [isSaveEnabled, setIsSaveEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const [recaptchaToken, setRecaptchaToken] = useState('');
  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleRecaptchaVerify = useCallback(async () => {
    if (!executeRecaptcha) {
      setSnackbar({ open: true, message: 'Please complete the reCAPTCHA verification.', severity: 'error' });
      return;
    }
    const recTokenResult = await executeRecaptcha('signInForm');
    setRecaptchaToken(recTokenResult);
    setIsSaveEnabled(true);
  }, [executeRecaptcha]);

  const handleSave = async () => {
    setLoading(true);

    await handleRecaptchaVerify();
    if (!recaptchaToken) {
      setSnackbar({ open: true, message: 'Please complete the reCAPTCHA verification.', severity: 'error' });
      setLoading(false);
      return;
    }

    const updatedUser: User = {
      ...user,
      uid: user?.uid || '',
      fname: user?.fname || '',
      lname: user?.lname || '',
      email: user?.email || '',
      phone: user?.phone || '',
      payerId: user?.payerId || 0,
      children: childrenData
    };

    const saveUserResponse = await saveUser(updatedUser);
    if (saveUserResponse) {
      setSnackbar({ open: true, message: 'Account successfully updated.', severity: 'success' });
      setLoading(false);
      navigate('/');
    } else {
      setSnackbar({ open: true, message: 'Failed to update account.', severity: 'error' });
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <div className="event-page p-5 mb-20">
      <Box
        component="form"
        sx={{
          '& .MuiTextField-root': { m: 1, width: '25ch' },
        }}
        autoComplete="off"
      >
        <div className="text-center mt-10">
          <ParentForm
            title="Parent Profile"
            disableInputs={true}
          />

          <hr />

          <div className="mt-5">
            <ChildrenForm onChildrenDataChange={(data: any[]) => setChildrenData(data as never[])} />
          </div>

          <div className="mt-10 mb-5">
            <GoogleReCaptcha
              onVerify={handleRecaptchaVerify}
            />
            <ButtonGroup variant="contained" aria-label="button group">
              <Button className="register-button gray"><Link to="/"> Cancel</Link></Button>
              <Button
                className="register-button"
                onClick={handleSave}
                disabled={!isSaveEnabled || loading}
              >
                Save
              </Button>
            </ButtonGroup>
          </div>
        </div>
        <CustomSnackbar
          open={snackbar.open}
          message={snackbar.message}
          severity={snackbar.severity}
          handleClose={handleCloseSnackbar}
        />
      </Box>
    </div>
  );
};

export default SignInPage;
